<script setup>
import { computed, ref } from "vue"
import VueDatePicker from "@vuepic/vue-datepicker"
import { es } from "date-fns/locale"

import HotelListaInput from "@/components/HotelListaInput.vue"
import MensajeWhatsapp from "@/components/MensajeWhatsapp.vue"
import ResultadoCotizacionPdf from "@/components/ResultadoCotizacionPdf.vue"
import TramoVueloInput from "@/components/TramoVueloInput.vue"
import { useCotizadorManual } from "@/composables/useCotizadorManual"
import { useGoogleFlightsAutofill } from "@/composables/useGoogleFlightsAutofill"
import { formatearMoneda, parsearMoneda } from "@/utils/formatoMoneda"

const {
  loading: cargandoAutocompletar,
  parsearUrl,
  tramoAFormulario
} = useGoogleFlightsAutofill()

const urlGoogleFlights = ref("")

const {
  destinosManual,
  destinoManual,
  config,

  destinoNombre,
  inicio,
  fin,
  pax,
  ninosManual,
  vuelos,
  traslados,
  origen,
  telefono,
  valorRecobro,
  vueloIda,
  vueloRegreso,
  vueloIntermedio,
  diasCombi1,
  diasCombi2,

  hotelesPorGrupo,
  hotelesGrupoA,
  hotelesGrupoB,

  inicioPlan,
  vueloNeto,
  nochesAdicionales,

  fechaSalida,
  cabinas,

  habitaciones,
  hotelesDesayuno,
  hotelesTodoIncluido,
  validacionesHabitaciones,
  hotelesBkk,
  diasBkk,

  hotelesCostaPorZona,
  zonasCostaEfectivas,

  resultadoTour,
  mensajeSinPdf,

  minFecha,
  maxFecha,
  minFechaRegreso,
  bloqueoRegreso,
  disabledDatesIda,

  generandoPdf,
  pdfUrl,
  errorPdf,
  generarPdf,

  agregarHotel,
  eliminarHotel,
  agregarHabitacion,
  eliminarHabitacion
} = useCotizadorManual()

const tipo = computed(() => config.value?.tipo)

const esFormularioBasico = computed(() =>
  ["estandar", "combinado", "gruposTravel", "otrosUsd", "sanAndres", "costa", "bkk"].includes(tipo.value)
)

const usaHabitaciones = computed(() => ["sanAndres", "bkk"].includes(tipo.value))

const vuelosFormateado = computed({
  get: () => (vuelos.value ? new Intl.NumberFormat("es-CO").format(vuelos.value) : ""),
  set: v => { vuelos.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

const trasladosFormateado = computed({
  get: () => (traslados.value ? new Intl.NumberFormat("es-CO").format(traslados.value) : ""),
  set: v => { traslados.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

const vueloNetoFormateado = computed({
  get: () => (vueloNeto.value ? new Intl.NumberFormat("es-CO").format(vueloNeto.value) : ""),
  set: v => { vueloNeto.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

const valorRecobroFormateado = computed({
  get: () => (valorRecobro.value ? new Intl.NumberFormat("es-CO").format(valorRecobro.value) : ""),
  set: v => { valorRecobro.value = Number(String(v).replace(/\./g, "").replace(/,/g, "")) || 0 }
})

async function autocompletarDesdeUrl() {
  const url = urlGoogleFlights.value.trim()
  if (!url) return

  const resultado = await parsearUrl(url)
  if (!resultado) return
  const { tramos, precio } = resultado

  const nuevoIda = tramoAFormulario(tramos[0])
  if (nuevoIda) Object.assign(vueloIda.value, { escalas: [], tipo: "directo", diasExtra: 0 }, nuevoIda)

  if (tramos.length === 2) {

    const nuevoRegreso = tramoAFormulario(tramos[1])
    if (nuevoRegreso) Object.assign(vueloRegreso.value, { escalas: [], tipo: "directo", diasExtra: 0 }, nuevoRegreso)

  } else if (tramos.length === 3) {

    if (config.value?.requiereTramoIntermedio) {
      const nuevoIntermedio = tramoAFormulario(tramos[1])
      if (nuevoIntermedio) Object.assign(vueloIntermedio.value, { escalas: [], tipo: "directo" }, nuevoIntermedio)
    }

    const nuevoRegreso = tramoAFormulario(tramos[2])
    if (nuevoRegreso) Object.assign(vueloRegreso.value, { escalas: [], tipo: "directo", diasExtra: 0 }, nuevoRegreso)
  }

  // "vuelos" es el costo total neto del vuelo para toda la cotización — el
  // precio que trae la página ya es el total del itinerario completo.
  if (precio != null) {
    vuelos.value = precio
  }

  urlGoogleFlights.value = ""
}
</script>

<template>
  <div class="container py-4" style="background-color: #f8f9fa; border-radius: 6px;">

    <!-- HEADER -->
    <div class="text-center mb-4">
      <h1 class="vip-title d-flex align-items-center justify-content-center gap-3">
        <span class="logo-vip">
          <i class="bi bi-calculator-fill plane-icon"></i>
        </span>
        <span class="title-animated">
          Cotizador Manual Viaja VIP
        </span>
      </h1>
    </div>

    <form class="row g-3" @submit.prevent>

      <!-- SELECTOR DE PLANTILLA (hoja del Excel) -->
      <div class="col-md-6">
        <label class="form-label">Plantilla de cotización</label>
        <select v-model="destinoManual" class="form-select">
          <option v-for="d in destinosManual" :key="d.value" :value="d.value">
            {{ d.label }}
          </option>
        </select>
      </div>

      <!-- DESTINO (texto libre cuando la plantilla no lo fija) -->
      <div v-if="esFormularioBasico" class="col-md-6">
        <label class="form-label">Destino</label>
        <input
          type="text"
          class="form-control"
          v-model="destinoNombre"
          :disabled="!!config.destinoFijo"
          placeholder="Ej: Rio de Janeiro"
        >
      </div>

      <!-- NOTAS DE LA HOJA -->
      <div v-if="config.notas?.length || config.restriccion" class="col-12">
        <div class="alert alert-warning py-2 mb-0">
          <div v-if="config.restriccion"><strong>{{ config.restriccion }}</strong></div>
          <div v-for="(n, i) in config.notas" :key="i" class="small">• {{ n }}</div>
        </div>
      </div>

      <!-- ================= FORMULARIO BÁSICO ================= -->
      <template v-if="esFormularioBasico">

        <div class="col-md-3">
          <label class="form-label">Inicio del viaje</label>
          <VueDatePicker v-model="inicio" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" :min-date="minFecha"
            :max-date="maxFecha" :disabled-dates="disabledDatesIda" />
          <small v-if="tipo === 'bkk'" class="text-muted">Salidas solo Dom/Mar/Jue/Vie</small>
        </div>

        <div class="col-md-3">
          <label class="form-label">Fin del viaje</label>
          <VueDatePicker v-model="fin" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" :min-date="minFechaRegreso"
            :max-date="maxFecha" :disabled="bloqueoRegreso" />
          <small v-if="bloqueoRegreso" class="text-muted">Se calcula automático según las noches indicadas</small>
        </div>

        <div v-if="!usaHabitaciones" class="col-md-3">
          <label class="form-label">Adultos</label>
          <input type="number" class="form-control" v-model.number="pax" min="1">
          <small class="text-muted">No incluir infantes</small>
        </div>

        <div v-if="!usaHabitaciones" class="col-md-3">
          <label class="form-label">Niños</label>
          <input type="number" class="form-control" v-model.number="ninosManual" min="0">
        </div>

        <div class="col-12">
          <div class="row g-3 row-cols-md-4">
            <div v-if="config.usaTraslados" class="col">
              <label class="form-label">Traslados (COP)</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="text" class="form-control" v-model="trasladosFormateado" placeholder="0">
              </div>
            </div>

            <!-- Datos para el PDF (no afectan el cálculo, van directo al template) -->
            <div class="col">
              <label class="form-label">Origen</label>
              <input type="text" class="form-control" v-model="origen" placeholder="BOG">
            </div>

            <div class="col">
              <label class="form-label">Teléfono (WhatsApp)</label>
              <input type="text" class="form-control" v-model="telefono" placeholder="3001234567">
            </div>

            <div class="col">
              <label class="form-label">Valor recobro (COP)</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="text" class="form-control" v-model="valorRecobroFormateado" placeholder="0">
              </div>
            </div>
          </div>
        </div>

        <template v-if="config.requiereDiasCombi">
          <div class="col-md-3">
            <label class="form-label">{{ config.labelsDiasCombi?.[0] || "Noches ciudad 1" }}</label>
            <input type="number" class="form-control" v-model.number="diasCombi1" min="0">
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ config.labelsDiasCombi?.[1] || "Noches ciudad 2" }}</label>
            <input type="number" class="form-control" v-model.number="diasCombi2" min="0">
          </div>
        </template>

        <!-- ============ TRAMOS DE VUELO ============ -->
        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header"><strong>Vuelos</strong></div>
            <div class="card-body">
              <div class="mb-3">
                <label class="form-label small mb-1">
                  Pegar URL de Google Flights (del vuelo seleccionado)
                </label>
                <div class="d-flex gap-2">
                  <input v-model="urlGoogleFlights" type="text" class="form-control"
                    placeholder="https://www.google.com/travel/flights/booking?tfs=..."
                    @keydown.enter.prevent="autocompletarDesdeUrl">
                  <button type="button" class="btn btn-outline-primary text-nowrap" :disabled="cargandoAutocompletar"
                    @click="autocompletarDesdeUrl">
                    {{ cargandoAutocompletar ? "Buscando..." : "Autocompletar" }}
                  </button>
                </div>
              </div>

              <TramoVueloInput v-model:vuelo="vueloIda" titulo="Ida" />

              <TramoVueloInput
                v-if="config.requiereTramoIntermedio"
                v-model:vuelo="vueloIntermedio"
                titulo="Tramo intermedio (Ciudad de México → Cancún)"
                :mostrar-dia-llegada="false"
                mostrar-fecha
                :fecha-min="inicio"
                :fecha-max="fin"
              />

              <TramoVueloInput v-model:vuelo="vueloRegreso" titulo="Regreso" />

              <div class="row justify-content-center mt-3">
                <div class="col-md-4">
                  <label class="form-label">Vuelos (COP, neto)</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="text" class="form-control" v-model="vuelosFormateado" placeholder="0">
                  </div>
                </div>
              </div>

              <p class="small text-muted mb-0 mt-2">
                Deja los campos de vuelo vacíos si no aplica — el PDF los muestra como "-".
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- ============ HOTELES: CTG/SMR (zonas propias) ============ -->
      <!-- Si el destino efectivo (código fijo o resuelto del texto libre en
           TC Nacional/Internacional/Grupos Travel/Otros USD) es Cartagena o
           Santa Marta, se muestran SUS zonas en vez de la lista genérica,
           sin importar qué plantilla esté seleccionada. -->
      <template v-if="zonasCostaEfectivas">
        <div class="col-12" v-for="z in zonasCostaEfectivas" :key="z.key">
          <HotelListaInput
            :titulo="z.label"
            :hoteles="hotelesCostaPorZona[z.key] ?? []"
            @agregar="agregarHotel(hotelesCostaPorZona[z.key])"
            @eliminar="i => eliminarHotel(hotelesCostaPorZona[z.key], i)"
          />
        </div>
      </template>

      <!-- ============ HOTELES: ESTANDAR / GRUPOS / OTROS ============ -->
      <template v-else-if="['estandar', 'gruposTravel', 'otrosUsd'].includes(tipo)">
        <div class="col-12" v-for="g in config.grupos" :key="g.key">
          <HotelListaInput
            :titulo="g.label"
            :hoteles="hotelesPorGrupo[g.key] ?? []"
            :moneda="tipo === 'otrosUsd' ? 'USD' : 'COP'"
            :labelValor="tipo === 'gruposTravel' ? 'Valor Total Neto' : 'Valor Total Comisionable'"
            @agregar="agregarHotel(hotelesPorGrupo[g.key])"
            @eliminar="i => eliminarHotel(hotelesPorGrupo[g.key], i)"
          />
        </div>
      </template>

      <!-- =================== COMBINADOS =================== -->
      <template v-if="tipo === 'combinado'">
        <div class="col-12">
          <HotelListaInput
            :titulo="config.grupoA.label"
            :hoteles="hotelesGrupoA"
            @agregar="agregarHotel(hotelesGrupoA)"
            @eliminar="i => eliminarHotel(hotelesGrupoA, i)"
          />
        </div>

        <div class="col-12" v-for="g in config.grupoB" :key="g.key">
          <HotelListaInput
            :titulo="g.label"
            :hoteles="hotelesGrupoB[g.key] ?? []"
            @agregar="agregarHotel(hotelesGrupoB[g.key])"
            @eliminar="i => eliminarHotel(hotelesGrupoB[g.key], i)"
          />
        </div>
      </template>

      <!-- =================== SAN ANDRÉS =================== -->
      <template v-if="tipo === 'sanAndres'">
        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between align-items-center">
              <strong>Habitaciones</strong>
              <button type="button" class="btn btn-sm btn-outline-primary"
                :disabled="habitaciones.length >= 10" @click="agregarHabitacion">
                <i class="bi bi-plus-lg"></i> Habitación
              </button>
            </div>
            <div class="card-body">
              <div v-for="(hab, i) in habitaciones" :key="i" class="row g-2 align-items-end mb-2">
                <div class="col-md-4">
                  <label class="form-label small mb-0">Habitación {{ i + 1 }} — Adultos</label>
                  <input type="number" class="form-control" v-model.number="hab.adultos" min="0" max="5">
                </div>
                <div class="col-md-4">
                  <label class="form-label small mb-0">Niños (2-9)</label>
                  <input type="number" class="form-control" v-model.number="hab.ninos" min="0" max="5">
                </div>
                <div class="col-md-3">
                  <span class="badge" :class="validacionesHabitaciones[i] === 'OK' ? 'bg-success' : 'bg-danger'">
                    {{ validacionesHabitaciones[i] }}
                  </span>
                </div>
                <div class="col-md-1">
                  <button type="button" class="btn btn-outline-danger w-100"
                    :disabled="habitaciones.length <= 1" @click="eliminarHabitacion(i)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <HotelListaInput
            titulo="Hoteles Solo Desayuno (netos Travel Compositor)"
            :hoteles="hotelesDesayuno"
            labelValor="Valor Total Neto"
            @agregar="agregarHotel(hotelesDesayuno)"
            @eliminar="i => eliminarHotel(hotelesDesayuno, i)"
          />
        </div>

        <div class="col-12">
          <HotelListaInput
            titulo="Hoteles Todo Incluido (netos Travel Compositor)"
            :hoteles="hotelesTodoIncluido"
            labelValor="Valor Total Neto"
            @agregar="agregarHotel(hotelesTodoIncluido)"
            @eliminar="i => eliminarHotel(hotelesTodoIncluido, i)"
          />
        </div>
      </template>

      <!-- =================== TAILANDIA (BKK) =================== -->
      <template v-if="tipo === 'bkk'">
        <div class="col-md-3">
          <label class="form-label">Días del plan</label>
          <select v-model.number="diasBkk" class="form-select">
            <option :value="12">12 días</option>
            <option :value="13">13 días</option>
          </select>
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between align-items-center">
              <strong>Habitaciones</strong>
              <button type="button" class="btn btn-sm btn-outline-primary"
                :disabled="habitaciones.length >= 10" @click="agregarHabitacion">
                <i class="bi bi-plus-lg"></i> Habitación
              </button>
            </div>
            <div class="card-body">
              <div v-for="(hab, i) in habitaciones" :key="i" class="row g-2 align-items-end mb-2">
                <div class="col-md-4">
                  <label class="form-label small mb-0">Habitación {{ i + 1 }} — Adultos</label>
                  <input type="number" class="form-control" v-model.number="hab.adultos" min="0" max="3">
                </div>
                <div class="col-md-4">
                  <label class="form-label small mb-0">Niños</label>
                  <input type="number" class="form-control" v-model.number="hab.ninos" min="0" max="3">
                </div>
                <div class="col-md-3"></div>
                <div class="col-md-1">
                  <button type="button" class="btn btn-outline-danger w-100"
                    :disabled="habitaciones.length <= 1" @click="eliminarHabitacion(i)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header"><strong>Hoteles (Turista / Premium / 5 Estrellas)</strong></div>
            <div class="card-body">
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel Turista — Nombre</label>
                  <input type="text" class="form-control" v-model="hotelesBkk.turista.nombre">
                </div>
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel Turista — Valor total (COP)</label>
                  <div class="input-group">
                    <span class="input-group-text">COP</span>
                    <input
                      type="text"
                      class="form-control"
                      :value="formatearMoneda(hotelesBkk.turista.valor)"
                      @input="hotelesBkk.turista.valor = parsearMoneda($event.target.value)"
                      placeholder="0"
                    >
                  </div>
                </div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel Premium — Nombre</label>
                  <input type="text" class="form-control" v-model="hotelesBkk.premium.nombre">
                </div>
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel Premium — Valor total (COP)</label>
                  <div class="input-group">
                    <span class="input-group-text">COP</span>
                    <input
                      type="text"
                      class="form-control"
                      :value="formatearMoneda(hotelesBkk.premium.valor)"
                      @input="hotelesBkk.premium.valor = parsearMoneda($event.target.value)"
                      placeholder="0"
                    >
                  </div>
                </div>
              </div>
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel 5 Estrellas — Nombre</label>
                  <input type="text" class="form-control" v-model="hotelesBkk.cincoEstrellas.nombre">
                </div>
                <div class="col-md-6">
                  <label class="form-label small mb-0">Hotel 5 Estrellas — Valor total (COP)</label>
                  <div class="input-group">
                    <span class="input-group-text">COP</span>
                    <input
                      type="text"
                      class="form-control"
                      :value="formatearMoneda(hotelesBkk.cincoEstrellas.valor)"
                      @input="hotelesBkk.cincoEstrellas.valor = parsearMoneda($event.target.value)"
                      placeholder="0"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- =================== TOUR (Turquía / Tailandia) =================== -->
      <template v-if="tipo === 'tour'">
        <div class="col-md-3">
          <label class="form-label">Fecha inicio del plan</label>
          <VueDatePicker v-model="inicioPlan" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Adultos</label>
          <input type="number" class="form-control" v-model.number="pax" min="1">
        </div>

        <div class="col-md-2">
          <label class="form-label">Niños</label>
          <input type="number" class="form-control" v-model.number="ninosManual" min="0">
        </div>

        <div class="col-md-3">
          <label class="form-label">Vuelo neto total personas</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input type="text" class="form-control" v-model="vueloNetoFormateado" placeholder="0">
          </div>
        </div>

        <div class="col-md-2">
          <label class="form-label">Noches adicionales</label>
          <input type="number" class="form-control" v-model.number="nochesAdicionales" min="0">
          <small class="text-muted">Travel Compositor comisionable</small>
        </div>

        <div v-if="resultadoTour" class="col-12">
          <div class="alert alert-info mb-0">
            Vuelos: <strong>{{ resultadoTour.ida.toLocaleDateString("es-CO") }}</strong>
            → <strong>{{ resultadoTour.regreso.toLocaleDateString("es-CO") }}</strong>
            <span v-if="resultadoTour.interno">
              (interno: {{ resultadoTour.interno.toLocaleDateString("es-CO") }})
            </span>
          </div>
        </div>
      </template>

      <!-- =================== CRUCERO =================== -->
      <template v-if="tipo === 'crucero'">
        <div class="col-md-3">
          <label class="form-label">Fecha de salida</label>
          <VueDatePicker v-model="fechaSalida" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
            format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Adultos (1 cabina)</label>
          <input type="number" class="form-control" v-model.number="pax" min="1" max="5">
        </div>

        <div class="col-md-2">
          <label class="form-label">Niños (1 cabina)</label>
          <input type="number" class="form-control" v-model.number="ninosManual" min="0" max="5">
        </div>

        <div class="col-12">
          <div class="card mb-3">
            <div class="card-header"><strong>Valores de cabina (USD)</strong></div>
            <div class="card-body">
              <div class="row g-2">
                <div v-for="(c, key) in cabinas" :key="key" class="col-md-3">
                  <label class="form-label text-capitalize"><strong>{{ key }}</strong></label>
                  <div class="input-group mb-1">
                    <span class="input-group-text small">Sin licores</span>
                    <input type="number" class="form-control" v-model.number="c.sin" min="0">
                  </div>
                  <div class="input-group">
                    <span class="input-group-text small">Con licores</span>
                    <input type="number" class="form-control" v-model.number="c.con" min="0">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

    </form>

    <!-- =================== RESULTADO =================== -->
    <!-- Turquía / Crucero: sin template de PDF compatible todavía, siguen
         funcionando como antes (mensaje de WhatsApp para copiar). El resto
         de plantillas genera el PDF con el template del destino elegido. -->
    <div class="mt-4">
      <MensajeWhatsapp
        v-if="config?.pdfNoDisponible"
        titulo="Mensaje de cotización"
        :mensaje="mensajeSinPdf"
      />

      <template v-else>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="generandoPdf"
          @click="generarPdf"
        >
          <span v-if="generandoPdf" class="spinner-border spinner-border-sm me-2"></span>
          Generar cotización (PDF)
        </button>

        <ResultadoCotizacionPdf
          :generando="generandoPdf"
          :pdf-url="pdfUrl"
          :error="errorPdf"
        />
      </template>
    </div>

  </div>
</template>

<style>
.vip-title {
  font-weight: 800;
}

.logo-vip {
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(135deg, #0d6efd, #6ea8fe);
  box-shadow: 0 10px 30px rgba(13, 110, 253, 0.25);
  position: relative;
  overflow: hidden;
}

.logo-vip::before {
  content: "";
  position: absolute;
  width: 120%;
  height: 120%;
  background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  transform: rotate(25deg);
  animation: shine 3s infinite;
}

.plane-icon {
  color: white;
  font-size: 1.4rem;
  z-index: 2;
}

.title-animated {
  background: linear-gradient(90deg, #0d6efd, #6ea8fe, #0d6efd);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 4s linear infinite;
  letter-spacing: -0.5px;
}

@keyframes shine {
  0% { left: -150%; }
  100% { left: 150%; }
}

@keyframes gradientMove {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}
</style>
